import { PortalUrls } from '@servicetitan/cp-ui';
import { IconNameType } from '@servicetitan/design-system';
import { inject, injectable } from '@servicetitan/react-ioc';
import { action, makeObservable, observable, reaction, runInAction, when } from 'mobx';
import { PortalAuthStore } from '../../auth/stores/portal-auth.store';
import { AppSettingsStore } from './app-settings.store';
import { BillingAccessStore } from './billing-access.store';
import { ContractStore } from './contract.store';
import { MembershipStore } from './membership.store';
import { ServiceAgreementStore } from './service-agreement.store';
interface NavigationLink {
    title: string;
    path: string;
    exact?: boolean;
    icon?: IconNameType;
    divider?: boolean;
}

@injectable()
export class NavigationStore {
    @observable navigationLinks: NavigationLink[] = [];

    constructor(
        @inject(PortalAuthStore) private authStore: PortalAuthStore,
        @inject(AppSettingsStore) private appSettingsStore: AppSettingsStore,
        @inject(MembershipStore) private membershipStore: MembershipStore,
        @inject(ServiceAgreementStore) private serviceAgreementStore: ServiceAgreementStore,
        @inject(BillingAccessStore) private billingAccessStore: BillingAccessStore,
        @inject(ContractStore) private contractStore: ContractStore
    ) {
        makeObservable(this);
        this.loadNavigationLinks();

        /*
         * loadNavigationLinks() bails out early while unauthenticated (nav is empty
         * pre-login) and otherwise only runs once, in the constructor. Nothing else
         * re-triggers it, so rebuild once auth completes (or the user logs out) to
         * pick up the now-authenticated settings.
         */
        reaction(
            () => this.authStore.authorized,
            () => {
                this.loadNavigationLinks();
            }
        );
    }

    @action
    private loadNavigationLinks = async () => {
        if (!this.authStore.authorized) {
            this.navigationLinks = [];
            return;
        }

        await when(
            () =>
                this.billingAccessStore.loaded &&
                this.membershipStore.loaded &&
                this.serviceAgreementStore.loaded &&
                this.appSettingsStore.loaded &&
                this.contractStore.loaded
        );

        const billingEnabled = this.billingAccessStore.isBillingEnabled;

        const out: NavigationLink[] = [
            {
                title: 'My Tasks',
                path: '/',
                exact: true,
                icon: 'home',
            },
        ];

        if (this.appSettingsStore.jobEnabled) {
            out.push({
                title: 'Jobs',
                path: PortalUrls.jobs.root,
                icon: 'job',
            });
        }

        if (billingEnabled && this.appSettingsStore.invoiceEnabled) {
            out.push({
                title: 'Invoices',
                path: PortalUrls.invoices.root,
                icon: 'st_invoice',
            });
        }

        if (
            billingEnabled &&
            this.appSettingsStore.proposalsEstimatesEnabled &&
            this.appSettingsStore.onlineEstimatesEnabled
        ) {
            out.push({
                title: this.contractStore.contractsExist ? 'Contracts' : 'Proposals',
                path: PortalUrls.proposals.root,
                icon: 'proposal',
            });
        }

        const { user } = this.authStore.portalStore;

        const pickMembership = user?.pickMembership;

        if (
            billingEnabled &&
            this.appSettingsStore.membershipSettings.enabled &&
            pickMembership &&
            this.membershipStore.membershipsExist
        ) {
            out.push({
                title: pickMembership('Memberships', 'Contracts'),
                path: pickMembership(
                    PortalUrls.memberships.rootMembership,
                    PortalUrls.memberships.rootContracts
                ),
                icon: 'card_membership',
            });
        }

        if (this.appSettingsStore.equipmentEnabled) {
            out.push({
                title: 'Equipment',
                path: PortalUrls.equipment.root,
                icon: 'st_equipment',
            });
        }

        if (
            billingEnabled &&
            this.appSettingsStore.serviceAgreementsEnabled &&
            this.serviceAgreementStore.serviceAgreementsExist
        ) {
            out.push({
                title: 'Service Agreements',
                path: PortalUrls.serviceAgreements.root,
                icon: 'st_service_agreements',
            });
        }

        if (billingEnabled && this.appSettingsStore.paymentMethodsEnabled) {
            out.push({
                title: 'Payment Methods',
                path: PortalUrls.paymentMethods.root,
                icon: 'credit_card',
                divider: true,
            });
        }

        runInAction(() => {
            this.navigationLinks = out;
        });
    };
}
